// src/components/bugReport/BugReportFAB.jsx
//
// Floating bottom-corner feedback cluster: "Report a Bug" (bug icon) and
// "Suggestion box" (lightbulb icon), stacked vertically. The bug modal
// captures description + URL/viewport/console errors/screenshot; the
// suggestion modal captures an idea that loads into the next Claude Code
// session for evaluation.
//
// Mounted in Layout (desktop) and MobileLayout (mobile).

import { useState } from 'react'
import { Bug, Lightbulb } from 'lucide-react'
import BugReportModal from './BugReportModal'
import SuggestionModal from './SuggestionModal'

export default function BugReportFAB({ bottomOffset = 'bottom-6', side = 'left', horizontalOffset, hiddenBelowLg = false }) {
  const [bugOpen, setBugOpen] = useState(false)
  const [suggestOpen, setSuggestOpen] = useState(false)
  // `horizontalOffset`, when supplied, overrides the plain side-based 24px
  // inset entirely (e.g. a responsive class string a host uses to clear a
  // docked panel that occupies the same corner at wider breakpoints — see
  // Layout.jsx's Home usage, 2026-09-30 repair, issue #178).
  const hPos = horizontalOffset || (side === 'right' ? 'right-6' : 'left-6')
  const btnClass = 'w-7 h-7 rounded-full bg-white border border-warm-200 shadow-md hover:shadow-lg hover:bg-warm-50 transition-all flex items-center justify-center text-warm-400'
  // `hiddenBelowLg`: below `lg` there is no fixed offset that clears an
  // expanded compact dock at EVERY viewport height (its Send button's
  // on-screen position moves with scroll/height, not with this button's own
  // offset), so a host whose below-`lg` layout can grow into this corner
  // hides the FAB entirely for as long as that's true, rather than picking
  // an offset that's only safe at some heights. Desktop's already-verified
  // `lg`+ placement is untouched either way (2026-09-30 repair, issue #178).
  const displayClass = hiddenBelowLg ? 'hidden lg:flex lg:flex-col' : 'flex flex-col'

  return (
    <>
      <div className={`fixed ${bottomOffset} ${hPos} z-40 ${displayClass} gap-2`}>
        <button
          type="button"
          onClick={() => setSuggestOpen(true)}
          title="Suggestion box — suggest new functionality"
          aria-label="Suggestion box"
          className={`${btnClass} hover:text-amber-500`}
        >
          <Lightbulb size={13} />
        </button>
        <button
          type="button"
          onClick={() => setBugOpen(true)}
          title="Report a bug"
          aria-label="Report a bug"
          className={`${btnClass} hover:text-terracotta-500`}
        >
          <Bug size={13} />
        </button>
      </div>
      {bugOpen && <BugReportModal isOpen={bugOpen} onClose={() => setBugOpen(false)} />}
      {suggestOpen && <SuggestionModal isOpen={suggestOpen} onClose={() => setSuggestOpen(false)} />}
    </>
  )
}
